/**
 * Facetten, Sortierwerte und Spaltenmasse der Bestellliste ("Bestellboard", `renderBoard`,
 * index.html:6991). Nachportierung nach Regel 1 des Phasenplans, Task 13.
 *
 * ================= Warum ein eigenes Modul =================
 *
 * Die acht Funktionen hier gehoeren fachlich zusammen (sie beschreiben EINE Tabelle) und ziehen
 * quer durch die bestehenden `lib/`-Module: `status.ts` (Statusbeschriftung, `orderListDue`),
 * `payments.ts` (Ratenbetrag, Ratenstatus), `suppliers.ts` (Waehrung), `money.ts` (Geldformat),
 * `format.ts` (Datumsformat). Sie in eines der fuenf zu legen, hiesse in jedem Fall, eine Kante in
 * den Importgraphen einzuziehen, die dort fachlich nichts zu suchen hat.
 *
 * ================= Signaturaenderungen (alle in WOERTLICHKEIT_AUSNAHMEN begruendet) =========
 *
 * Das Original liest fuenf Globals, die es hier nicht gibt. Sie werden als Argumente hereingereicht:
 *
 * | Global im Original | hier |
 * |---|---|
 * | `STATUS_MAP` | `definitionen` (Ergebnis von `setStatusDefinitions`) |
 * | `DB.suppliers` (ueber `orderCurrency`/`fmtMoney`) | `suppliers` |
 * | `DB.orderListColumns` | `orderListColumns` |
 * | `ORDER_LIST_WIDTHS` (aus `localStorage`, Schluessel `hubOrderListWidths`) | `breiten` |
 *
 * Die Spaltenbreiten kommen ausdruecklich NICHT aus `localStorage` heraus in `lib/` an - `lib/` hat
 * keinen Aussenbezug. Die Kapsel dafuer ist `state/arbeitsplatz.ts` (Task 14).
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. GESTRICHEN seit Auftrag "Bestellboard-Filter datentypgerecht bauen" (2026-09-01): `orderListFacet`
 *    und `orderListSortValue` behandelten bis dahin zehn Schluessel, die es in der Liste NICHT mehr
 *    gibt (`tracking`, `invoice`, `p1amount`, `p1due`, `p1status`, `p2amount`, `p2due`, `p2status`,
 *    `paymentStatus`, `terms`, alle zehn weiterhin in `REMOVED_ORDER_LIST_KEYS`,
 *    `config/board.ts:113-124`) - reine Woertlichkeit, im lebenden Pfad unerreichbar (siehe
 *    Kommentar dort). Dieser Auftrag hat die zehn toten `case`-Zweige TATSAECHLICH entfernt statt sie
 *    nur unerreichbar zu lassen: eine unbekannte Spalte (auch eine der zehn) faellt seither auf den
 *    `default`-Zweig (Behandlung wie ein Custom-Feld). Nachweis, dass sie WIRKLICH weg sind (nicht nur
 *    weiterhin durch den Ladepfad gefiltert): `test/befunde/entfernteSpaltenschluessel.test.ts`.
 * 2. (Punkte 2/3 der Vorfassung betrafen `p1status`/`p2status` - mit Punkt 1 oben entfallen.)
 * 3. `activeOrderListColumns` sortiert mit `Number(a.position || 0)`: eine Spalte mit
 *    `position: 0` und eine ohne `position` sind damit nicht unterscheidbar. Bei gleichem Wert
 *    entscheidet die (stabile) Ausgangsreihenfolge.
 * 4. `orderListColumnWidth` klemmt mit `Math.max(min, Math.min(max, ...))`. Steht `min` ueber `max`
 *    (im Auslieferungszustand nie der Fall), gewaenne `min` - `max` waere wirkungslos.
 * 5. `orderListTableWidth` summiert die GEKLEMMTEN Breiten. Die Tabelle ist damit immer genau so
 *    breit wie die Summe ihrer `<col>`-Breiten, auch bei unsinnigen gespeicherten Werten.
 *
 * ================= Datentypgerechte Spaltenfilter (Auftrag 2026-09-01) =================
 *
 * Bis hierher war jeder Spaltenfilter ein Exakttreffer auf `orderListFacet(...).value` (natives
 * `<select>`, `state/adresse.ts#Ansicht.board.spaltenfilter: Record<string,string>`). Das passte
 * nicht zum tatsaechlichen Datentyp der Spalte: ein Datum verdient einen Von/Bis-Bereich, ein Betrag
 * ebenso (je Waehrung getrennt, keine Umrechnung), ein Boolean ("Zahlung anstoßen") eine echte
 * Ja/Nein/Alle-Auswahl statt der Magic Strings `"payment"`/`"__EMPTY__"`, und eine feste kleine
 * Aufzaehlung (Status) eine Mehrfachauswahl aus der KANONISCHEN Liste (`definitionen.STATUSES`),
 * nicht nur den in `facetOrders` sichtbaren Werten. „Ware eingebucht" war bis 2026-10-20 ebenfalls
 * Boolean und ist seither eine eigene Filterart ("eingebucht": Datumsbereich + Zustaende).
 *
 * `orderListFilterKind` entscheidet je Spaltenschluessel, welcher der vier Datentypen gilt -
 * `orderListSpaltenfilterAktiv`/`orderListZeilePasstFilter` werten den dazu passenden
 * `Spaltenfilterwert` aus. Die URL-Kodierung dieser Werte (Bereichsfilter als `f.<key>.von`/`.bis`,
 * Mehrfachauswahl als wiederholter Query-Schluessel) steht in `state/adresse.ts` - siehe dortiger
 * Kommentar zu `Spaltenfilter` in `lesAbfrage`/`schreibAbfrage`.
 */
import { fmtDate } from "./format.ts";
import { fmtMoney } from "./money.ts";
import { orderTotal } from "./payments.ts";
import { orderListDue } from "./status.ts";
import type { StatusDefinitionen } from "./status.ts";
import { orderCurrency } from "./suppliers.ts";
import type { Order, OrderItem, Supplier } from "./types.ts";
import { zollinstruktionenAngestossen } from "./zollinstruktionen.ts";
import {
  DEFAULT_CUSTOM_ORDER_LIST_COLUMN_SIZE,
  DEFAULT_ORDER_LIST_COLUMNS,
  ORDER_LIST_COLUMN_SIZES,
} from "../config/board.ts";
import type { ColumnSize, OrderListColumn } from "../config/board.ts";

export type { Order, Supplier };

/**
 * Die EINE Zustandsableitung einer Bestellzeile fuer das Bestellboard (Auftrag 07, E2/E4/E5):
 * `"deleted"`, `"archived"` oder `"active"`.
 *
 * ================= Warum `deleted` GEWINNT =================
 *
 * Eine Bestellung kann zugleich `archived` UND `deleted_in_jtl` sein (JTL archiviert eine
 * Bestellung, spaeter wird sie dort geloescht, das Archivflag bleibt stehen). Die Zeile gehoert
 * dann in GENAU EINE Kategorie - sonst erschiene sie bei gleichzeitig gewaehltem "Archiviert" und
 * "Geloescht" zweimal bzw. liesse sich ueber den Archivzweig weiterhin ziehen. `deleted` schlaegt
 * deshalb `archived` (Auftrag 07, E2/E4: "erscheint genau einmal").
 *
 * ================= Warum `=== true` statt einer Wahrheitspruefung =================
 *
 * `deleted_in_jtl` kommt erst mit Auftrag 05 aus der API (`api/jtlBestellungen.ts:151/160/458`,
 * snake_case - NICHT `deletedInJtl`). Bis dahin ist das Feld an jeder Bestellung `undefined`, und
 * `undefined` bedeutet ausdruecklich "nicht geloescht": die strenge Pruefung auf `true` macht
 * diesen Auftrag unabhaengig von Auftrag 05. Sie ist zugleich die einzige Form, die den Wert am
 * `Order`-Typ vorbeibringt - `Order` (`lib/types.ts:13`) traegt eine Indexsignatur
 * `[feld: string]: unknown`, also liefert jeder Zugriff `unknown`. KEIN `as`-Cast (Auftragsverbot):
 * der Vergleich `unknown === true` ist ohne ihn zulaessig.
 */
export function orderListZustand(o: Order): "active" | "archived" | "deleted" {
  if (o.deleted_in_jtl === true) return "deleted";
  if (o.archived) return "archived";
  return "active";
}

/**
 * `true`, wenn ALLE Positionen der Bestellung eine offene Menge von 0 tragen - abgeleitet aus den
 * JTL-Positionsmengen (OrderItem.anzahlOffen), NICHT mehr aus dem entfernten Hub-Feld
 * `deliveryBooked`/`bookedDate` (siehe backend/migrations/026_order_extras_booked_entfernen.sql).
 * Eine Bestellung ohne Positionen gilt NICHT als vollstaendig geliefert - es gibt nichts zu
 * beurteilen (anders als "jede von null Positionen ist geliefert", was `every` auf einem leeren
 * Array liefern wuerde).
 *
 * KEIN `?? 0`-Rueckfall, sondern `?? NaN` plus `Number.isFinite`: eine Position ohne lesbare
 * `anzahlOffen` gilt als NICHT geliefert, nicht als "0 offen". Das muss BEIDE Luecken abdecken, die
 * die reale JTL-Form kennt - nicht nur `undefined` (Fixture ohne das Feld), sondern auch `null`, die
 * TATSAECHLICH deklarierte Form von `OrderItem.anzahlOffen` (`frontend/src/api/jtlBestellungen.ts`)
 * und der reale Rueckgabewert von `alsZahl(zeile.fAnzahlOffen)` (`backend/src/jtl/bestellungen.ts`),
 * sobald JTL `fAnzahlOffen = fMenge - fMengeGeliefert` mit einem NULL-Operanden nicht berechnen
 * kann. `Number(null)` waere `0`, `0 <= 0` waere `true` - eine Bestellung mit lauter `anzahlOffen:
 * null` zeigte damit faelschlich "vollstaendig geliefert". Mit einem `?? 0`-Rueckfall (oder ohne
 * Rueckfall UND ohne `Number.isFinite`) waere das ebenso der Fall: eine fehlende/unlesbare Menge
 * saehe dann genauso aus wie eine tatsaechlich auf 0 gelaufene.
 */
export function vollstaendigGeliefert(o: Order): boolean {
  const items = (o.items ?? []) as { anzahlOffen?: unknown }[];
  if (items.length === 0) return false;
  return items.every((item) => {
    const n = Number(item.anzahlOffen ?? NaN);
    return Number.isFinite(n) && n <= 0;
  });
}

/** Der Lieferstatus EINER Position (Auftrag "Lieferstatus-Icons + Uhrzeit", 2026-08-28). */
export type LieferStatus = "offen" | "teilgeliefert" | "komplett";

/**
 * `true`, wenn die Position eine gelieferte Menge > 0 traegt (`Number.isFinite`-abgesichert, KEIN
 * `?? 0`-Rueckfall - dieselbe Absicherung wie `vollstaendigGeliefert` oben). Eigene, kleine
 * Ableitung statt Teil von `lieferStatusPosition` allein: sowohl `lieferStatusPosition` (Positions-
 * Icon) als auch der Gesamtstatus-Banner in `Anlieferungsliste.tsx` (rot/gelb, "mindestens eine
 * Position mit gelieferter Menge > 0?") brauchen GENAU diese Bedingung - `Anlieferungsliste.tsx`
 * ruft diese Funktion auf, statt dieselbe Zeile ein zweites Mal hinzuschreiben (Pruefung,
 * Nachbesserung 2026-08-28, Punkt 4).
 */
export function hatGelieferteMenge(item: OrderItem): boolean {
  const geliefert = Number(item.mengeGeliefert ?? NaN);
  return Number.isFinite(geliefert) && geliefert > 0;
}

/**
 * `"komplett"`, wenn `anzahlOffen <= 0` (dieselbe `Number.isFinite`-Absicherung wie
 * `vollstaendigGeliefert` oben - KEIN `?? 0`-Rueckfall: eine unlesbare/`null`-Menge gilt NICHT als
 * komplett). Sonst `"teilgeliefert"`, wenn `hatGelieferteMenge(item)` zutrifft. Sonst `"offen"` -
 * der Rueckfall fuer "noch nichts geliefert" UND fuer eine Position, deren Mengen sich gar nicht
 * lesen lassen (weder `anzahlOffen` noch `mengeGeliefert` sind dann verlaesslich als "> 0" zu
 * werten, also faellt sie auf den nicht-positiven Zustand zurueck statt stillschweigend als
 * "komplett" oder "teilgeliefert" zu gelten).
 */
export function lieferStatusPosition(item: OrderItem): LieferStatus {
  const offen = Number(item.anzahlOffen ?? NaN);
  if (Number.isFinite(offen) && offen <= 0) return "komplett";
  return hatGelieferteMenge(item) ? "teilgeliefert" : "offen";
}

/**
 * Die drei Zustaende der Spalte „Ware eingebucht" (Asana „Ware eingebucht Knopf - Sehr wichtig fuer
 * Steuerberater", Auftrag 2026-10-20):
 * - `"vollstaendig"`: alle Positionen ohne offene Menge (`vollstaendigGeliefert`),
 * - `"teilweise"`: nicht vollstaendig, aber mindestens eine Position mit gelieferter Menge > 0
 *   (`hatGelieferteMenge`),
 * - `"nichts"`: sonst.
 */
export type EingebuchtZustand = "vollstaendig" | "teilweise" | "nichts";

/** Alle drei Zustaende in kanonischer Reihenfolge - auch die Reihenfolge der Filter-Checkboxen und der URL. */
export const EINGEBUCHT_ZUSTAENDE: readonly EingebuchtZustand[] = ["vollstaendig", "teilweise", "nichts"];

/**
 * Der Zustand einer Bestellung in der Spalte „Ware eingebucht". `vollstaendigGeliefert` hat Vorrang;
 * eine Bestellung ohne Positionen oder mit unlesbaren Mengen ist „nichts" - nie „teilweise" oder
 * „vollstaendig" (dieselbe Absicherung wie dort: eine fehlende Menge ist keine gelieferte).
 */
export function eingebuchtZustand(o: Order): EingebuchtZustand {
  if (vollstaendigGeliefert(o)) return "vollstaendig";
  const items = (o.items ?? []) as OrderItem[];
  return items.some(hatGelieferteMenge) ? "teilweise" : "nichts";
}

/**
 * Der Kalendertag der LETZTEN Einbuchung der Bestellung (`Order.letzteEinbuchungAm`, ISO
 * `YYYY-MM-DD`, aus `GET /api/jtl/orders` - der spaeteste Tag ueber alle Positionen), sonst `null`.
 * Ein Zeitanteil wird abgeschnitten, ein unlesbarer oder fremder Wert ergibt `null` - ein Datum wird
 * nie geraten und nie als „Invalid Date" gezeigt.
 */
export function letzteEinbuchungVon(o: Order): string | null {
  const roh = o.letzteEinbuchungAm;
  if (typeof roh !== "string") return null;
  const tag = roh.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(tag) ? tag : null;
}

/** Ein Facettenwert der Spaltenfilter: `value` filtert, `label` steht im Auswahlfeld. */
export interface OrderListFacet {
  value: string;
  label: string;
}

/**
 * Gespeicherte Spaltenbreiten (`localStorage`-Schluessel `hubOrderListWidths`, index.html:4639).
 * Als Argument hereingereicht, siehe Kopfkommentar.
 */
export type OrderListWidths = Readonly<Record<string, unknown>>;

/**
 * index.html:7258 - `STATUS_MAP` ist hier `definitionen` (drittes Argument), `DB.suppliers` (ueber
 * `orderCurrency`/`fmtMoney`) ist `suppliers` (viertes Argument).
 */
export function orderListFacet(
  o: Order,
  key: string,
  definitionen: StatusDefinitionen,
  suppliers: readonly Supplier[] | null | undefined,
): OrderListFacet {
  const empty = { value: "__EMPTY__", label: "Ohne Wert" };
  const dateFacet = (value: unknown) =>
    value ? { value: value as string, label: fmtDate(value as string) } : empty;
  switch (key) {
    case "name":
      return o.orderNo
        ? { value: o.orderNo as string, label: o.orderNo as string }
        : empty;
    case "dsvShipmentNo":
      return o.dsvShipmentNo
        ? { value: o.dsvShipmentNo as string, label: o.dsvShipmentNo as string }
        : empty;
    case "due":
      return dateFacet(orderListDue(o));
    case "supplier":
      return o.supplier
        ? { value: o.supplier as string, label: o.supplier as string }
        : empty;
    case "status":
      return {
        value: (o.status as string) || "__EMPTY__",
        label: definitionen.STATUS_MAP[o.status as string]?.label || "Ohne Status",
      };
    case "invoiceNo":
      return o.invoiceNo
        ? { value: o.invoiceNo as string, label: o.invoiceNo as string }
        : empty;
    case "value": {
      const amount = orderTotal(o);
      return amount
        ? {
            value: `${orderCurrency(o, suppliers)}:${amount}`,
            label: fmtMoney(o, amount, suppliers),
          }
        : empty;
    }
    case "etdPi":
      return dateFacet(o.etdPi);
    case "etd":
      return dateFacet(o.etd);
    case "eta":
      return dateFacet(o.eta);
    case "payment":
      return o.paymentInitiatedAt
        ? { value: "payment", label: "Zahlung angestoßen" }
        : empty;
    case "zoll":
      return zollinstruktionenAngestossen(o)
        ? { value: "zoll", label: "Zollinstruktionen angestoßen" }
        : empty;
    case "booked":
      return vollstaendigGeliefert(o) ? { value: "booked", label: "vollständig geliefert" } : empty;
    default: {
      const value = String((o.customFields as Record<string, unknown>)?.[key] || "").trim();
      return value ? { value, label: value } : empty;
    }
  }
}

/**
 * index.html:7301 - `definitionen` (drittes Argument) und `suppliers` (viertes) werden nur an
 * `orderListFacet` durchgereicht.
 *
 * Eigenheit: `__EMPTY__` wird IMMER ans Ende sortiert, unabhaengig von der Richtung - der
 * Vergleicher prueft es vor dem `localeCompare`. Die uebrigen Werte stehen nach `label`, nicht nach
 * `value`; zwei verschiedene Werte mit gleicher Beschriftung behalten damit ihre Einfuegereihenfolge.
 */
export function orderListFacetOptions(
  key: string,
  orders: readonly Order[],
  definitionen: StatusDefinitionen,
  suppliers: readonly Supplier[] | null | undefined,
): OrderListFacet[] {
  const values = new Map<string, string>();
  for (const o of orders) {
    const facet = orderListFacet(o, key, definitionen, suppliers);
    if (!values.has(facet.value)) values.set(facet.value, facet.label);
  }
  return [...values.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) =>
      a.value === "__EMPTY__"
        ? 1
        : b.value === "__EMPTY__"
          ? -1
          : a.label.localeCompare(b.label, "de", { numeric: true, sensitivity: "base" }),
    );
}

// ================= Datentypgerechte Spaltenfilter (NEUBAU, Auftrag 2026-09-01) =================
//
// Kein Bestandsvorbild: `index.html` kennt nur den Exakttreffer aus `orderListFacet(...).value` -
// siehe Kopfkommentar. Die Typen und Funktionen unten sind komplett neu; die URL-Kodierung der
// Filterwerte steht in `state/adresse.ts` (`lesAbfrage`/`schreibAbfrage`, Kommentar dort).

/** Die vier Datumsspalten - Bereichsfilter statt Exakttreffer. */
export const ORDER_LIST_DATE_KEYS: ReadonlySet<string> = new Set(["due", "etdPi", "etd", "eta"]);

/** Die einzige Betragsspalte - Bereichsfilter, getrennt je angezeigter Waehrung (keine Umrechnung). */
export const ORDER_LIST_MONEY_KEYS: ReadonlySet<string> = new Set(["value"]);

/** Die Boolean-Spalten („Zahlung anstoßen" und „Zollinstruktionen anstoßen") - Ja/Nein/Alle statt der
 *  Magic Strings "payment"/"zoll"/"__EMPTY__". „Ware eingebucht" (`booked`) war bis 2026-10-20 ebenfalls
 *  Boolean und ist seither `ORDER_LIST_EINGEBUCHT_KEYS`. */
export const ORDER_LIST_BOOLEAN_KEYS: ReadonlySet<string> = new Set(["payment", "zoll"]);

/**
 * Die Spalte „Ware eingebucht" (Auftrag 2026-10-20): Datumsbereich auf die letzte Einbuchung PLUS
 * Mehrfachauswahl der Zustaende (vollstaendig / teilweise / nicht eingebucht) - ein eigener Filter-
 * Datentyp, weder Boolean noch reiner Datumsbereich. Als Set gefuehrt wie die Nachbarn, auch wenn es
 * heute nur einen Schluessel gibt.
 */
export const ORDER_LIST_EINGEBUCHT_KEYS: ReadonlySet<string> = new Set(["booked"]);

/**
 * Die feste kleine Aufzaehlung "Status" - Mehrfachauswahl aus der KANONISCHEN Liste
 * (`definitionen.STATUSES`), nicht nur den in `facetOrders` sichtbaren Werten. Transportart
 * (`order_extras.transport_mode`, DB-Enum see/air/road) gehoert fachlich in dieselbe Kategorie, ist
 * aber KEIN Spaltenfilter-Schluessel dieser Liste: `config/board.ts#DEFAULT_ORDER_LIST_COLUMNS`
 * fuehrt keine "transportart"-Spalte, der Transportart-Filter sitzt app-weit als Chip in
 * `App/Kopfzeile.tsx` (siehe Auftragsbrief, Abschnitt "Ausdruecklich NICHT anfassen") - dieses Set
 * bleibt deshalb bewusst bei genau einem Eintrag.
 */
export const ORDER_LIST_ENUM_KEYS: ReadonlySet<string> = new Set(["status"]);

/**
 * Die sechs Filter-Datentypen - `Spaltenkopf.tsx` waehlt daraus eines von FUENF Widgets: "enum" und
 * "text" teilen sich `MehrfachauswahlFilter` (nur die Herkunft der Optionsliste unterscheidet sich,
 * kanonisch vs. aus `facetOrders` abgeleitet). "eingebucht" (Spalte „Ware eingebucht") hat ein eigenes
 * Widget aus Datumsbereich und Zustandsauswahl.
 */
export type OrderListFilterKind = "date" | "money" | "boolean" | "eingebucht" | "enum" | "text";

/**
 * Ordnet einen Spaltenschluessel seinem Filter-Datentyp zu - alles nicht Gelistete ist "text"
 * (Freitext mit Wertevorrat: Lieferant, Bestellnummer, jedes Custom-Feld aus
 * `order_extras.custom_fields`).
 */
export function orderListFilterKind(key: string): OrderListFilterKind {
  if (ORDER_LIST_DATE_KEYS.has(key)) return "date";
  if (ORDER_LIST_MONEY_KEYS.has(key)) return "money";
  if (ORDER_LIST_BOOLEAN_KEYS.has(key)) return "boolean";
  if (ORDER_LIST_EINGEBUCHT_KEYS.has(key)) return "eingebucht";
  if (ORDER_LIST_ENUM_KEYS.has(key)) return "enum";
  return "text";
}

/**
 * Ein Von/Bis-Bereich, beide Enden offen erlaubt (leer/fehlend = keine Grenze).
 *
 * `ohneWert` (Nachbesserung W1/W2, Pruefung "Bestellboard-Filter datentypgerecht bauen"): EXKLUSIV -
 * zeigt NUR Zeilen OHNE lesbaren Wert fuer dieses Feld, `von`/`bis` werden dabei ignoriert (siehe
 * `orderListZeilePasstFilter`). Das ist die Entsprechung der alten Facettenoption "Ohne Wert"
 * (`__EMPTY__`) fuer Datums-/Betragsspalten, die das fruehere `<select>` anbot, die neuen
 * Bereichsfilter-Widgets aber sonst nicht ausdruecken koennten - ohne diesen Zustand liefe ein alter
 * Tieflink mit `__EMPTY__` (z. B. `f.due=__EMPTY__`) ins Leere statt "Zeilen ohne Wert" zu zeigen.
 */
export interface Bereichsfilter {
  von?: string;
  bis?: string;
  ohneWert?: boolean;
}

/**
 * Ein Spaltenfilterwert - die Form haengt vom Datentyp der Spalte ab (siehe `orderListFilterKind`):
 * - "enum"/"text": `string[]` - Mehrfachauswahl, ODER-verknuepft (ein leeres Array steht fuer "kein
 *   Filter" und wird von `state/adresse.ts` nie geschrieben - siehe dort).
 * - "date": `Bereichsfilter` - Von/Bis auf den Rohwert aus `orderListFacet(...).value` (ISO-8601,
 *   lexikalisch sortierbar), oder `{ohneWert: true}` fuer "nur Zeilen ohne Datum".
 * - "money": `Record<string, Bereichsfilter>` - Von/Bis je Waehrungskuerzel, KEINE Umrechnung: eine
 *   Bestellung in einer nicht im Filter vertretenen Waehrung bleibt sichtbar. Der reservierte,
 *   waehrungsunabhaengige Schluessel `"__EMPTY__"` traegt `{ohneWert: true}` fuer "nur Zeilen ohne
 *   Betrag" - kein echtes Waehrungskuerzel, siehe `orderListZeilePasstFilter`.
 * - "boolean": `boolean` - Ja (`true`)/Nein (`false`); FEHLT der Schluessel im `Spaltenfilter`-Record
 *   ganz, gilt das als "Alle" (kein Filter).
 * - "eingebucht": `EingebuchtFilter` - Von/Bis auf das Datum der letzten Einbuchung plus die Auswahl
 *   der Zustaende.
 */
export type Spaltenfilterwert =
  | string[]
  | Bereichsfilter
  | Record<string, Bereichsfilter>
  | boolean
  | EingebuchtFilter;

/**
 * Der Filter der Spalte „Ware eingebucht" (Auftrag 2026-10-20), beide Haelften unabhaengig:
 * - `von`/`bis`: Datumsbereich auf die LETZTE Einbuchung (`letzteEinbuchungVon`), einschliesslich,
 *   jede Seite einzeln optional. Ist eine Seite gesetzt, fallen Zeilen OHNE Datum heraus - wie bei den
 *   Datumsspalten ist „irgendwann" keine gueltige Antwort auf „in diesem Zeitraum". Ein „Nur ohne
 *   Wert" gibt es hier bewusst nicht: „nicht eingebucht" deckt das ab.
 * - `zustaende`: Mehrfachauswahl, ODER-verknuepft. Fehlt sie, ist sie leer oder enthaelt sie ALLE drei
 *   Zustaende, schraenkt sie nicht ein („alle an = kein Filter", `wirksameZustaende`).
 */
export interface EingebuchtFilter {
  von?: string;
  bis?: string;
  zustaende?: readonly EingebuchtZustand[];
}

/**
 * Die Zustaende, auf die ein `EingebuchtFilter` TATSAECHLICH einschraenkt - `null`, wenn er es nicht
 * tut (keine Auswahl, alle drei, oder nur unbekannte Werte aus einer fremden Adresse). Die Reihenfolge
 * ist die kanonische (`EINGEBUCHT_ZUSTAENDE`), doppelte Eintraege fallen weg.
 */
export function wirksameZustaende(filter: EingebuchtFilter): EingebuchtZustand[] | null {
  const gewaehlt = EINGEBUCHT_ZUSTAENDE.filter((zustand) => filter.zustaende?.includes(zustand));
  return gewaehlt.length === 0 || gewaehlt.length === EINGEBUCHT_ZUSTAENDE.length ? null : gewaehlt;
}

/** `Ansicht.board.spaltenfilter` (`state/adresse.ts`) - ein Wert je Spaltenschluessel. */
export type Spaltenfilter = Record<string, Spaltenfilterwert>;

/**
 * `true`, wenn dieser Filterwert tatsaechlich einschraenkt - die Grundlage fuer
 * `BoardSeite.tsx#activeColumnFilters` (Zaehler "n von m Bestellungen", Knopf "Filter
 * zuruecksetzen"). Ersetzt den bisherigen blossen Wahrheitstest auf einen String.
 */
export function orderListSpaltenfilterAktiv(key: string, wert: Spaltenfilterwert | undefined): boolean {
  if (wert === undefined) return false;
  const kind = orderListFilterKind(key);
  if (kind === "boolean") return typeof wert === "boolean";
  if (kind === "eingebucht") {
    const filter = wert as EingebuchtFilter;
    return Boolean(filter.von || filter.bis || wirksameZustaende(filter));
  }
  if (kind === "date") {
    const bereich = wert as Bereichsfilter;
    return Boolean(bereich.von || bereich.bis || bereich.ohneWert);
  }
  if (kind === "money") {
    const bereiche = wert as Record<string, Bereichsfilter>;
    return Object.values(bereiche).some((b) => Boolean(b?.von || b?.bis || b?.ohneWert));
  }
  return (wert as string[]).length > 0;
}

/**
 * Der Wahrheitswert einer Boolean-Spalte (`ORDER_LIST_BOOLEAN_KEYS`) - je Schluessel eine eigene
 * Ableitung. `orderListZeilePasstFilter` rief hier bis Task 1218171668075282 (2026-09-04) fest
 * `vollstaendigGeliefert(order)` auf, unabhaengig vom uebergebenen `key` - solange es mit "booked"
 * nur EINE Boolean-Spalte gab, fiel das nicht auf. Mit "payment" als zweiter Boolean-Spalte haette
 * dieselbe feste Verdrahtung den neuen Filter STILL nach "vollstaendig geliefert" statt nach
 * "Zahlung anstoßen" ausgewertet - siehe die Regressionspruefung in orderList.test.ts.
 *
 * Seit dem Auftrag „Ware eingebucht mit Datum" (2026-10-20) ist „booked" KEINE Boolean-Spalte mehr
 * (`ORDER_LIST_EINGEBUCHT_KEYS`); „payment" und - seit der Spalte „Zollinstruktionen anstoßen" - „zoll"
 * (`zollinstruktionenAngestossen`, erledigter Zoll-Task) sind die Boolean-Spalten. Der Rueckfall auf
 * `false` fuer jeden anderen Schluessel ist bewusst: ein Schluessel, der hier nicht gelistet ist, soll
 * nie wieder STILL nach „vollstaendig geliefert" auswerten.
 */
function orderListBooleanValue(order: Order, key: string): boolean {
  if (key === "payment") return !!order.paymentInitiatedAt;
  if (key === "zoll") return zollinstruktionenAngestossen(order);
  return false;
}

/**
 * `true`, wenn `order` den gesetzten Filter `wert` an Spalte `key` erfuellt - die neue Semantik je
 * Datentyp (ersetzt den bisherigen `orderListFacet(...).value === value`-Exakttreffer in
 * `BoardSeite.tsx`):
 * - Mehrfachauswahl ("enum"/"text"): ODER-verknuepft - die Zeile passt, wenn ihr Facettenwert IN der
 *   Auswahl steht. Ein leeres Array laesst (wie `orderListSpaltenfilterAktiv` oben) jede Zeile durch.
 * - Bereich ("date"): `ohneWert` ist EXKLUSIV und schlaegt `von`/`bis` (nur Zeilen OHNE Datum passen,
 *   siehe Kopfkommentar `Bereichsfilter`). Sonst `von <= x <= bis`, beide Grenzen einzeln optional -
 *   eine Bestellung OHNE Wert (Facette `"__EMPTY__"`) faellt dabei aus JEDEM gesetzten
 *   Datumsbereich heraus: "irgendwann" ist keine gueltige Antwort auf "in diesem Zeitraum".
 * - Bereich je Waehrung ("money"): der reservierte Schluessel `"__EMPTY__"` (siehe Kopfkommentar
 *   `Bereichsfilter`) ist ebenfalls EXKLUSIV und waehrungsunabhaengig - zeigt nur Zeilen ohne
 *   lesbaren Betrag, unabhaengig von etwaig zusaetzlich gesetzten Waehrungsbereichen. Sonst wie
 *   gehabt: nur fuer die Waehrung(en), die der Filter fuehrt; eine andere Waehrung bleibt
 *   UNGEFILTERT sichtbar (keine Umrechnung, siehe Auftragsbrief).
 * - Boolean ("boolean"): ausgewertet ueber `orderListBooleanValue(order, key)` - je Schluessel eine
 *   eigene Ableitung, kein fester Vergleich mit `vollstaendigGeliefert(order)` mehr (siehe deren
 *   Kopfkommentar).
 */
export function orderListZeilePasstFilter(
  order: Order,
  key: string,
  wert: Spaltenfilterwert,
  definitionen: StatusDefinitionen,
  suppliers: readonly Supplier[] | null | undefined,
): boolean {
  const kind = orderListFilterKind(key);
  if (kind === "boolean") return orderListBooleanValue(order, key) === (wert as boolean);
  if (kind === "eingebucht") {
    const filter = wert as EingebuchtFilter;
    const erlaubt = wirksameZustaende(filter);
    if (erlaubt && !erlaubt.includes(eingebuchtZustand(order))) return false;
    if (filter.von || filter.bis) {
      const tag = letzteEinbuchungVon(order);
      if (tag === null) return false;
      if (filter.von && tag < filter.von) return false;
      if (filter.bis && tag > filter.bis) return false;
    }
    return true;
  }
  if (kind === "date") {
    const { von, bis, ohneWert } = wert as Bereichsfilter;
    const facet = orderListFacet(order, key, definitionen, suppliers);
    if (ohneWert) return facet.value === "__EMPTY__";
    if (facet.value === "__EMPTY__") return false;
    if (von && facet.value < von) return false;
    if (bis && facet.value > bis) return false;
    return true;
  }
  if (kind === "money") {
    const bereiche = wert as Record<string, Bereichsfilter>;
    if (bereiche.__EMPTY__?.ohneWert) {
      // Exklusiv, waehrungsunabhaengig - siehe Kopfkommentar `Bereichsfilter`. Ein zusaetzlich
      // gesetzter Waehrungsbereich wird dabei ignoriert (beides gleichzeitig waere ohnehin
      // widerspruechlich: "nur ohne Wert" UND "100-500 EUR" schliessen sich fachlich aus).
      return !orderTotal(order);
    }
    const waehrung = orderCurrency(order, suppliers);
    const bereich = bereiche[waehrung];
    if (!bereich) return true; // diese Waehrung fuehrt der Filter nicht - ungefiltert durchlassen
    const betrag = orderTotal(order);
    if (!betrag) return false; // ein Bereich ist fuer die Waehrung gesetzt, aber kein Betrag lesbar
    if (bereich.von && betrag < Number(bereich.von)) return false;
    if (bereich.bis && betrag > Number(bereich.bis)) return false;
    return true;
  }
  const werte = wert as string[];
  if (!werte.length) return true;
  return werte.includes(orderListFacet(order, key, definitionen, suppliers).value);
}

/**
 * Die kanonische Status-Optionsliste fuer die Mehrfachauswahl (Spaltenschluessel "status") - ALLE
 * konfigurierten Status (`definitionen.STATUSES`, in `position`-Reihenfolge), nicht nur die in
 * `facetOrders` sichtbaren. "Ohne Status" (Facettenwert `"__EMPTY__"`) steht IMMER am Ende, auch wenn
 * gerade keine sichtbare Bestellung ohne Status ist - dieselbe "Leerwert ans Ende"-Konvention wie
 * `orderListFacetOptions`.
 */
export function orderListStatusOptionen(definitionen: StatusDefinitionen): OrderListFacet[] {
  return [
    ...definitionen.STATUSES.map((status) => ({ value: status.id ?? "", label: status.label })),
    { value: "__EMPTY__", label: "Ohne Status" },
  ];
}

/**
 * Die Waehrungskuerzel, die unter den uebergebenen Bestellungen tatsaechlich vorkommen (sortiert,
 * doppelte entfernt) - der Betrags-Bereichsfilter zeigt ein Von/Bis-Paar je Kuerzel (siehe
 * Kopfkommentar `Spaltenfilterwert`, "money"). Eine Bestellung ohne Betrag (`orderTotal(o) === 0`)
 * zaehlt hier NICHT mit, obwohl `orderCurrency` ihr trotzdem eine Waehrung zuordnet - sonst zeigte
 * die Spalte ein Waehrungspaar, das nie einen Treffer haben kann.
 */
export function orderListMoneyWaehrungen(
  orders: readonly Order[],
  suppliers: readonly Supplier[] | null | undefined,
): string[] {
  const gefunden = new Set<string>();
  for (const o of orders) {
    if (orderTotal(o)) gefunden.add(orderCurrency(o, suppliers));
  }
  return [...gefunden].sort();
}

/**
 * index.html:7343 - `STATUS_MAP` ist hier `definitionen` (drittes Argument). Ein `suppliers`-
 * Argument braucht diese Funktion NICHT: anders als `orderListFacet` formatiert sie kein Geld,
 * sondern liefert bei `value`/`p1amount`/`p2amount` den rohen Zahlenwert.
 *
 * Eigenheit: der Rueckgabetyp ist gemischt. `value`, `payment` und `zoll` liefern immer eine ZAHL;
 * `status` liefert bei bekanntem Status ebenfalls eine Zahl (`position`), bei unbekanntem/fehlendem
 * Status aber `""` (Leerwert, damit der Comparator ihn ans Ende sortiert, siehe dortiger Kommentar).
 * `booked` liefert das ISO-Datum der letzten Einbuchung, ohne Datum ebenfalls `""`.
 * Alle uebrigen Schluessel liefern durchgehend eine Zeichenkette. Der generische Comparator
 * (`BoardSeite.tsx`/`renderBoard` in `index.html`) unterscheidet danach (`typeof av === 'number'`)
 * und behandelt Leerwerte (`""`/`null`/`undefined`) vorrangig, unabhaengig vom Typ der Nicht-Leerwerte.
 */
export function orderListSortValue(
  o: Order,
  key: string,
  definitionen: StatusDefinitionen,
): string | number {
  switch (key) {
    case "name":
      return String(o.orderNo || "").toLowerCase();
    case "dsvShipmentNo":
      return String(o.dsvShipmentNo || "").toLowerCase();
    case "due":
      return orderListDue(o);
    case "supplier":
      return String(o.supplier || "").toLowerCase();
    case "status":
      // Fachliche Reihenfolge statt Alphabet (Asana "Status sortieren auf Bestellübersicht
      // fachlich nicht nach Alphabet"): `position` aus DEFAULT_BOARD_COLUMNS/STATUS_MAP, nicht das
      // Anzeigelabel. Unbekannter/fehlender Status hat keinen Treffer in STATUS_MAP und landet ans
      // Ende - analog zu "Ohne Status" in orderListStatusOptionen.
      //
      // Pruefrunde 1, Befund 2: NICHT `Number.MAX_SAFE_INTEGER` zurueckgeben - das ist eine normale
      // Zahl und durchlaeuft im generischen Comparator (BoardSeite.tsx, `orders`-`useMemo`) den
      // NUMERISCHEN Zweig, der MIT der Sortierrichtung multipliziert wird. Bei absteigender
      // Sortierung wanderte ein unbekannter Status dadurch nach OBEN statt nach unten. `""` loest
      // dagegen denselben richtungsunabhaengigen Leerwert-Zweig aus wie bei den anderen Schluesseln
      // dieser Funktion (z. B. `"name"`/`"supplier"` oben: fehlendes Feld -> `""`).
      return definitionen.STATUS_MAP[o.status as string]?.position ?? "";
    case "invoiceNo":
      return String(o.invoiceNo || "").toLowerCase();
    case "value":
      return orderTotal(o);
    case "etdPi":
      return (o.etdPi as string) || "";
    case "etd":
      return (o.etd as string) || "";
    case "eta":
      return (o.eta as string) || "";
    case "payment":
      return o.paymentInitiatedAt ? 1 : 0;
    case "zoll":
      return zollinstruktionenAngestossen(o) ? 1 : 0;
    case "booked":
      // Das Datum der letzten Einbuchung (Auftrag „Ware eingebucht mit Datum", 2026-10-20). Ohne
      // Datum `""` - der generische Comparator (`BoardSeite.tsx`) sortiert Leerwerte unabhaengig von
      // der Richtung ans Ende. Vorher: 1/0 nach `vollstaendigGeliefert`.
      return letzteEinbuchungVon(o) ?? "";
    default:
      return String((o.customFields as Record<string, unknown>)?.[key] || "").toLowerCase();
  }
}

/** index.html:7504 - `DB.orderListColumns` ist hier das Argument `orderListColumns`. */
export function activeOrderListColumns(
  orderListColumns: readonly OrderListColumn[] | null | undefined,
): OrderListColumn[] {
  const columns = orderListColumns?.length ? orderListColumns : DEFAULT_ORDER_LIST_COLUMNS;
  return columns
    .slice()
    .sort((a, b) => Number(a.position || 0) - Number(b.position || 0))
    .filter((column) => column.visible !== false);
}

/** index.html:7509 */
export function orderListColumnSize(key: string): ColumnSize {
  return ORDER_LIST_COLUMN_SIZES[key] || DEFAULT_CUSTOM_ORDER_LIST_COLUMN_SIZE;
}

/** index.html:7513 - `ORDER_LIST_WIDTHS` ist hier das Argument `breiten`. */
export function orderListColumnWidth(key: string, breiten: OrderListWidths): number {
  const size = orderListColumnSize(key);
  return Math.max(size.min, Math.min(size.max, Number(breiten[key]) || size.width));
}

/**
 * index.html:7518 - `ORDER_LIST_WIDTHS` ist hier `breiten` (zweites Argument). Der Vorgabewert des
 * Originals (`= activeOrderListColumns()`) entfaellt: `activeOrderListColumns` braucht hier selbst
 * ein Argument, und beide Aufrufstellen im Original uebergeben `columns` ohnehin ausdruecklich.
 */
export function orderListTableWidth(
  columns: readonly OrderListColumn[],
  breiten: OrderListWidths,
): number {
  return columns.reduce((sum, column) => sum + orderListColumnWidth(column.key, breiten), 0);
}
